import { createEvent, createStore, restore, sample } from 'effector'
import {
  AddWinnersRequest,
  GoldenMayStatus,
  GoldenMayStatusResponse,
  Winner,
  WinnersResponse,
} from './types'
import { fx } from 'src/libs'
import { buildQuery, ListRequest } from 'src/pages/goldenMay2026/utils'
import {
  NotificationPayload,
  showNotification,
} from 'src/pages/goldenMay2026/model'

export const goldenMayStatusLoad = createEvent<void | any>()
export const winnersListLoad = createEvent<ListRequest>()
export const onChangeWinnerList = createEvent<Partial<ListRequest>>()
export const changeStatusSubmit = createEvent<void | any>()
export const addWinnersSubmit = createEvent<AddWinnersRequest>()
export const showError = createEvent<Error>()

export const getGoldenMayStatusFx = fx<void, GoldenMayStatusResponse>({
  method: 'GET',
  url: `/promo/golden_may_2026/promo`,
})

export const changeStatusFx = fx({
  method: 'PUT',
  url: `/promo/golden_may_2026/promo/{id}`,
})

export const getWinnersFx = fx<ListRequest, WinnersResponse>({
  method: 'GET',
  url: `/promo/golden_may_2026/winner`,
})

export const addWinnersFx = fx<AddWinnersRequest, void>({
  method: 'POST',
  url: `/promo/golden_may_2026/winner/bulk`,
})

export const $goldenMayStatus = createStore<GoldenMayStatus[]>([])
  .on(getGoldenMayStatusFx.doneData, (_, res) => res.body.items)
  .on(getGoldenMayStatusFx.failData, () => [{ status: 'ended' }])

export const $winners = createStore<Winner[]>([]).on(
  getWinnersFx.doneData,
  (_, res) => res.body.items,
)

export const $totalCountOfWinners = restore(
  getWinnersFx.doneData.map((a) => a.body.total),
  0,
)

export const $winnersParams = createStore<ListRequest>({
  page: 1,
  limit: 10,
  orderBy: '',
  orderDirection: '',
  searchText: {},
  filters: {},
}).on(onChangeWinnerList, (state, payload) => ({ ...state, ...payload }))

sample({
  clock: [goldenMayStatusLoad, changeStatusFx.done],
  target: getGoldenMayStatusFx,
})

sample({
  clock: changeStatusSubmit,
  fn: () => ({
    url: '/promo/golden_may_2026/promo/1',
    body: { status: 'results' },
  }),
  target: changeStatusFx,
})

sample({
  clock: addWinnersSubmit,
  fn: ({ prizeType, tickets }) => {
    return {
      url: `/promo/golden_may_2026/winner/bulk`,
      body: { prizeType, tickets },
    }
  },
  target: addWinnersFx,
})

sample({
  clock: $winnersParams,
  fn: buildQuery,
  target: getWinnersFx,
})

sample({
  clock: addWinnersFx.doneData,
  source: $winnersParams,
  fn: buildQuery,
  target: getWinnersFx,
})

sample({
  clock: changeStatusFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Status changed to results',
  }),
  target: showNotification,
})

sample({
  clock: addWinnersFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Winner added',
  }),
  target: showNotification,
})

sample({
  clock: showError,
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'File error',
    description: error.message,
  }),
  target: showNotification,
})

sample({
  clock: [
    getGoldenMayStatusFx.failData,
    getWinnersFx.failData,
    changeStatusFx.failData,
    addWinnersFx.failData,
  ],
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'Error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})
